import React, { useEffect, useCallback, useRef } from "react";
import { SwipeableDrawer, useMediaQuery, useTheme } from "@mui/material";
import { observer } from "mobx-react";

import DocumentViewer from "./DocumentViewer";
import MessageEditor, { MessageEditorRef } from "./MessageEditor";
import { ContractPreview } from "../ui/ContractPreview";
import { PolicyPreview } from "../ui/PolicyPreview";
import { IMessage } from "../../services";
import type { AppealLetterFormat } from "../../services/apiService";
import type { IFileAttachment } from "../../types/chat";
import SidebarDelimiter from "../SidebarDelimiter";
import { useChatCategoryStore } from "../../hooks/useChatCategoryStore";
import { useChatStore } from "../../hooks/useChatStore";
import { useAppStore } from "../../hooks/useAppStore";
import ResizePointerShim from "./ResizePointerShim";

export type PanelType = "editor" | "viewer" | "policy" | "contract" | null;

interface EditorPanelProps {
  messageEditorRef: React.RefObject<MessageEditorRef | null>;
  editingContent: string;
  editorPosition: "left" | "right";
  onClose: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
  onTogglePosition: () => void;
  onSave: (content: string) => Promise<void>;
  onChange: (content?: string) => void;
  /** Flush pending edits, then download the server-rendered letter (REV-2047). */
  onDownload?: (format: AppealLetterFormat) => Promise<void>;
  metadata?: IMessage["metadata"];
  /** An older version: shown, not edited. */
  readOnly?: boolean;
  /** Changes when a different version opens, so the editor remounts and its
   * pending debounced save for the previous version is dropped with it. */
  versionKey?: string;
}

interface ViewerPanelProps {
  initialPage?: number | null;
  rotation?: Record<number, number>;
  fileName?: string;
  displayName?: string;
  fileContent?: string;
  fileContentType?: string;
  rawContent?: string;
  /** When set (e.g. chat file preview), DocumentViewer can offer Download like ChatMessageFileBox. */
  fileAttachment?: IFileAttachment | null;
  viewerPosition: "left" | "right";
  onClose: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
  onTogglePosition: () => void;
  isLoading: boolean;
}

export interface SidePanelContentProps {
  activePanel: PanelType;
  position?: "left" | "right";
  editor?: EditorPanelProps;
  viewer?: ViewerPanelProps;
}

const SidePanelContent: React.FC<SidePanelContentProps> = observer(
  ({ activePanel, position = "right", editor, viewer }) => {
    const theme = useTheme();
    const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));

    const {
      sidePanelWidth,
      isSidePanelResizing,
      setSidePanelWidth,
      setIsSidePanelResizing,
      isPoliciesDocumentsDrawerOpen,
    } = useChatCategoryStore();
    const chatStore = useChatStore();
    const { contractToPreview } = useAppStore();

    // Refs for resize calculations
    const positionRef = useRef(position);
    const asideRef = useRef<HTMLElement>(null);

    useEffect(() => {
      positionRef.current = position;
    }, [position]);

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        const containerRef = document.getElementById("chat-container-ref");
        if (!isSidePanelResizing || !containerRef) return;

        const containerRect = containerRef.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const newWidth =
          positionRef.current === "left"
            ? e.clientX - containerRect.left
            : containerRect.right - e.clientX;
        const newPercent = (newWidth / containerWidth) * 100;

        setSidePanelWidth(newPercent);
      },
      [isSidePanelResizing],
    );

    const handleMouseUp = useCallback(() => {
      setIsSidePanelResizing(false);
    }, []);

    useEffect(() => {
      if (isSidePanelResizing) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      }

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }, [isSidePanelResizing, handleMouseMove, handleMouseUp]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      setIsSidePanelResizing(true);
    }, []);

    const getOnClose = () => {
      if (activePanel === "editor" && editor) return editor.onClose;
      if (activePanel === "viewer" && viewer) return viewer.onClose;

      return () => {};
    };

    const renderPanelContent = () => {
      switch (activePanel) {
        case "editor":
          if (!editor) return null;
          return (
            <MessageEditor
              key={editor.versionKey}
              ref={editor.messageEditorRef}
              initialContent={editor.editingContent}
              position={editor.editorPosition}
              onClose={editor.onClose}
              onBack={editor.onBack}
              canGoBack={editor.canGoBack}
              onTogglePosition={editor.onTogglePosition}
              onSave={editor.onSave}
              onChange={editor.onChange}
              onDownload={editor.onDownload}
              metadata={editor.metadata}
              disableEditMode={chatStore.isInputDisabled || !!editor.readOnly}
              claimId={chatStore.chatDetails?.claim_number || undefined}
            />
          );
        case "viewer":
          if (!viewer) return null;
          return (
            <DocumentViewer
              initialPage={viewer.initialPage}
              rotation={viewer.rotation}
              fileName={viewer.fileName}
              displayName={viewer.displayName}
              fileContent={viewer.fileContent}
              fileContentType={viewer.fileContentType}
              rawContent={viewer.rawContent}
              fileAttachment={viewer.fileAttachment}
              position={viewer.viewerPosition}
              onClose={viewer.onClose}
              onBack={viewer.onBack}
              canGoBack={viewer.canGoBack}
              onTogglePosition={viewer.onTogglePosition}
              isLoading={viewer.isLoading}
            />
          );
        case "policy":
          return <PolicyPreview />;
        case "contract":
          return <ContractPreview />;
        default:
          return null;
      }
    };

    const panelContent = renderPanelContent();

    if (!activePanel) return null;

    // For tablet and smaller screens, use SwipeableDrawer
    if (isTabletOrSmaller) {
      return (
        <SwipeableDrawer
          anchor={position}
          open={activePanel !== null}
          onClose={getOnClose()}
          onOpen={() => {}} // Required prop but we don't need it
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              maxWidth: "100vw",
              boxSizing: "border-box",
            },
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <div className="h-full flex flex-col bg-white">{panelContent}</div>
        </SwipeableDrawer>
      );
    }

    // Display the open panel rail for policy questions
    if (
      (activePanel === "policy" && !isPoliciesDocumentsDrawerOpen) ||
      (activePanel === "contract" && !contractToPreview)
    ) {
      return panelContent;
    }

    const delimiterTogglePositionFn =
      activePanel === "editor"
        ? editor?.onTogglePosition
        : activePanel === "viewer"
          ? viewer?.onTogglePosition
          : undefined;

    // For desktop, render with resize delimiter
    return (
      <>
        <ResizePointerShim active={isSidePanelResizing && !isTabletOrSmaller} />
        <aside
          ref={asideRef}
          className="h-full min-h-0 flex flex-row bg-white"
          style={{
            width: `${sidePanelWidth}%`,
            flexShrink: 0,
            transition: isSidePanelResizing ? "none" : "width 150ms ease-out",
          }}
        >
          {position === "right" && (
            <SidebarDelimiter
              onTogglePosition={delimiterTogglePositionFn}
              onResizeStart={handleMouseDown}
              isResizing={isSidePanelResizing}
            />
          )}
          <div
            className="flex-1 min-w-0 flex flex-col"
            style={{ width: `calc(${sidePanelWidth}% - 12px)` }}
          >
            {panelContent}
          </div>
          {position === "left" && (
            <SidebarDelimiter
              onTogglePosition={delimiterTogglePositionFn}
              onResizeStart={handleMouseDown}
              isResizing={isSidePanelResizing}
            />
          )}
        </aside>
      </>
    );
  },
);

export default SidePanelContent;
